import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { currentFiscalYear, selectableFiscalYears } from '@budgetmonkey/shared'

/**
 * Which budget year the app is looking at — REQUIREMENTS.md §11.
 *
 * There are always at least two live years in practice. A P-Card cycle
 * straddles the 1 July rollover, so the July statement carries charges from
 * the year that just ended; and next year's codes are seeded before this one
 * closes. Until now every screen asked for `currentFiscalYear()` directly,
 * which made last year's ledger unreachable — and a charge moved into it
 * simply disappeared.
 *
 * The choice is remembered across reloads, because reviewing last year is a
 * task that takes more than one page. It is deliberately *not* remembered
 * across the rollover: a stored 2026 is silently wrong on 1 July 2027, so
 * anything outside the offered range falls back to today's year.
 */

const STORAGE_KEY = 'budgetmonkey:fiscalYear'

interface FiscalYearContext {
  fiscalYear: number
  setFiscalYear: (year: number) => void
  /** True when looking at anything other than the year we are actually in. */
  isCurrent: boolean
}

const Context = createContext<FiscalYearContext | null>(null)

function readStored(): number | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === null) return null
  const parsed = Number(raw)
  // Range-checked rather than trusted: a value stored last July is a year the
  // picker no longer offers, and restoring it would put the app in a state the
  // menu cannot show or undo.
  return selectableFiscalYears().includes(parsed) ? parsed : null
}

export function FiscalYearProvider({ children }: { children: React.ReactNode }) {
  const [fiscalYear, setFiscalYear] = useState(() => readStored() ?? currentFiscalYear())

  useEffect(() => {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, String(fiscalYear))
  }, [fiscalYear])

  const value = useMemo<FiscalYearContext>(
    () => ({
      fiscalYear,
      setFiscalYear,
      isCurrent: fiscalYear === currentFiscalYear(),
    }),
    [fiscalYear],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

/**
 * The selected budget year.
 *
 * Falls back to today's year rather than throwing when no provider is above
 * it: a screen rendered outside the shell should still show something, and
 * today's year is the honest default.
 */
export function useFiscalYearContext(): FiscalYearContext {
  const context = useContext(Context)
  if (context !== null) return context
  const year = currentFiscalYear()
  return { fiscalYear: year, setFiscalYear: () => {}, isCurrent: true }
}

/** Just the year, which is what almost every caller wants. */
export function useFiscalYear(): number {
  return useFiscalYearContext().fiscalYear
}

/**
 * The selected year, unless a caller names one.
 *
 * Data hooks take this rather than defaulting to `currentFiscalYear()`, which
 * is what previously pinned every screen to today no matter what the menu
 * said. An explicit argument still wins, for the few places that genuinely
 * mean one particular year.
 */
export function useResolvedFiscalYear(explicitYear?: number): number {
  const selected = useFiscalYear()
  return explicitYear ?? selected
}
